import React, { useState } from "react";
import { TypedToolInvocation } from "../utils";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { CheckIcon, Loader2Icon } from "lucide-react";
import { ReactComponent as GoogleDriveIcon } from "../../../../static/icons/google-drive.svg";
import { useImportGoogleDriveFilesMutation } from "../../../../data/mutations/import-google-drive-files";
import { useUploadFileMutation } from "../../../../data/mutations/upload-file";
import { useChatActions } from "../chat-subpage";
import Spinner from "../../../../components/ui/spinner";
import { cn } from "../../../../lib/utils";
import { InferChatToolResult } from "tekkr-common/dist/model/chats/tools";

type GoogleDriveResult = InferChatToolResult<"getGoogleDriveContext">;
export type FileSuggestion = Extract<GoogleDriveResult, { type: "file_suggestions" }>["files"][number];

const KEEP_TOP_FILE_COUNT = 5;

function formatFileDate(dateStr: string | null): string {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
        return `Today, ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    }
    const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
    if (date.getFullYear() !== now.getFullYear()) {
        options.year = "numeric";
    }
    return date.toLocaleDateString([], options);
}

export function GoogleDriveContextToolInvocation({
    invocation,
}: {
    invocation: TypedToolInvocation<"getGoogleDriveContext">;
}) {
    if (invocation.state !== "result" || !invocation.result) {
        return (
            <div className="flex items-center gap-3 rounded-md border bg-accent p-2 text-sm font-medium text-muted-foreground">
                <GoogleDriveIcon className="h-5 w-5" />
                <span>Searching Google Drive...</span>
                <Spinner className="h-4 w-4" />
            </div>
        );
    }
    return null;
}

interface SuggestedContextPanelProps {
    files: FileSuggestion[];
    toolCallId: string;
    compareEnabled: boolean;
    disabled?: boolean;
    onDismiss: () => void;
    onStartCompare: (sourceToolCallId: string, curatedExternalIds: string[]) => void;
    comparePending: boolean;
}

export function SuggestedContextPanel({
    files,
    toolCallId,
    compareEnabled,
    disabled,
    onDismiss,
    onStartCompare,
    comparePending,
}: SuggestedContextPanelProps) {
    const [selected, setSelected] = useState<Set<string>>(() => new Set(files.map((f) => f.externalId)));
    const [importing, setImporting] = useState(false);
    const [imported, setImported] = useState(false);
    const importFilesMutation = useImportGoogleDriveFilesMutation();
    const uploadFileMutation = useUploadFileMutation();
    const chatActions = useChatActions();

    const toggleFile = (externalId: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(externalId)) next.delete(externalId);
            else next.add(externalId);
            return next;
        });
    };

    const selectAllFiles = () => {
        setSelected(new Set(files.map((file) => file.externalId)));
    };

    const keepTopFiles = () => {
        setSelected(new Set(files.slice(0, KEEP_TOP_FILE_COUNT).map((file) => file.externalId)));
    };

    const shouldShowCompare = compareEnabled && files.length >= 3 && selected.size > 0 && selected.size < files.length;

    const handleGenerate = async () => {
        const selectedFiles = files.filter((f) => selected.has(f.externalId));
        if (selectedFiles.length === 0) return;

        setImporting(true);
        try {
            const importedFiles = await importFilesMutation.mutateAsync({
                files: selectedFiles.map((f) => ({
                    fileId: f.externalId,
                    mimeType: f.mimeType ?? undefined,
                    name: f.title ?? undefined,
                })),
            });

            const fileNames: string[] = [];
            for (const fileContent of importedFiles) {
                if (fileContent.error) continue;
                const blob = new Blob([fileContent.content], { type: "text/plain" });
                const file = new File([blob], fileContent.name ?? "google-drive-file.txt", { type: "text/plain" });
                await uploadFileMutation.mutateAsync({ file });
                fileNames.push(fileContent.name ?? "file");
            }

            setImported(true);
            chatActions.sendMessage(
                `I've added ${fileNames.length} file${fileNames.length === 1 ? "" : "s"} from Google Drive: ${fileNames.join(", ")}. Please use them to answer my question.`
            );
            setTimeout(onDismiss, 1500);
        } finally {
            setImporting(false);
        }
    };

    const handleCompare = () => {
        const curatedExternalIds = files.filter((f) => selected.has(f.externalId)).map((f) => f.externalId);
        if (curatedExternalIds.length > 0) {
            onStartCompare(toolCallId, curatedExternalIds);
        }
    };

    if (imported) {
        return (
            <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                <CheckIcon className="mb-2 h-8 w-8 text-green-500" />
                <p className="font-medium">Files added to conversation</p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            <div className="shrink-0 border-b p-4">
                <div className="flex items-center gap-2">
                    <GoogleDriveIcon className="h-4 w-4 shrink-0" />
                    <span className="font-semibold">Suggested context</span>
                    <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                        {files.length} file{files.length === 1 ? "" : "s"}
                    </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">From your Drive · uncheck to remove</p>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
                <div className="mb-3 flex items-center justify-between gap-2 px-1">
                    <span className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Selection
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={keepTopFiles}
                            disabled={disabled || importing || comparePending || files.length <= KEEP_TOP_FILE_COUNT}
                            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                        >
                            Keep top {Math.min(KEEP_TOP_FILE_COUNT, files.length)}
                        </button>
                        <button
                            type="button"
                            onClick={selectAllFiles}
                            disabled={disabled || importing || comparePending || selected.size === files.length}
                            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                        >
                            Select all
                        </button>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    {files.map((file) => (
                        <FileCard
                            key={file.externalId}
                            file={file}
                            checked={selected.has(file.externalId)}
                            onToggle={() => toggleFile(file.externalId)}
                            disabled={disabled || importing || comparePending}
                        />
                    ))}
                </div>
            </div>

            <div className="shrink-0 border-t p-4">
                <p className="mb-3 text-center text-sm text-muted-foreground">
                    {selected.size} file{selected.size === 1 ? "" : "s"} selected
                </p>

                <Button
                    className="w-full"
                    disabled={disabled || selected.size === 0 || importing || comparePending}
                    onClick={shouldShowCompare ? handleCompare : handleGenerate}
                >
                    {importing || comparePending ? (
                        <>
                            <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <CheckIcon className="mr-2 h-4 w-4" />
                            Generate with {selected.size} file{selected.size === 1 ? "" : "s"}
                        </>
                    )}
                </Button>

                <button
                    onClick={onDismiss}
                    disabled={disabled || importing || comparePending}
                    className="mt-2 w-full text-center text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                    Skip and generate without files
                </button>
            </div>
        </div>
    );
}

function FileCard({
    file,
    checked,
    onToggle,
    disabled,
}: {
    file: FileSuggestion;
    checked: boolean;
    onToggle: () => void;
    disabled: boolean;
}) {
    const dateStr = formatFileDate(file.lastModifiedAt);

    return (
        <label
            className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50",
                checked ? "border-primary/50 bg-primary/5" : "border-border",
                disabled && "pointer-events-none opacity-50"
            )}
        >
            <Checkbox checked={checked} onCheckedChange={onToggle} disabled={disabled} className="mt-0.5" />
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" title={file.title ?? "Untitled"}>
                    {file.title ?? "Untitled"}
                </p>
                {dateStr && <p className="text-xs text-muted-foreground">{dateStr}</p>}
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{file.relevanceReason}</p>
            </div>
        </label>
    );
}
